/**
 * Milk-It Variant Picker (checkbox version)
 * Slim implementation: renders 2 checkboxes and switches variants by SKU using the existing <select> options.
 *
 * Required option data attributes (provided by product option snippet):
 * - option[data-variant-sku]
 * - option[data-variant-id]
 */
(() => {
  'use strict';

  const getScopeRoot = () => document.querySelector('.quick-view--open') || document;

  const qs = (selector, root = null) => (root || getScopeRoot()).querySelector(selector);

  const getVariantSelect = (root = null) => {
    const scope = root || getScopeRoot();
    return (
      scope.querySelector('variant-selects[data-primary] select[id^="Option-"][name^="options["]') ||
      scope.querySelector('select[id^="Option-"][name^="options["]') ||
      null
    );
  };

  const dispatchVariantChanged = (root, variant) => {
    if (!variant) return;
    const scope = root || getScopeRoot();
    const variantSelects = scope.querySelector('variant-selects');
    if (!variantSelects) return;

    variantSelects.dispatchEvent(new CustomEvent('variant:changed', {
      detail: { variant, variantId: variant.id },
      bubbles: true
    }));
  };
  
  const dispatchChange = (el) => {
    if (!el || !el.dispatchEvent) return;
    try {
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } catch (_e) {
      // Older iOS Safari fallback
      try {
        const evt = document.createEvent('Event');
        evt.initEvent('change', true, true);
        el.dispatchEvent(evt);
      } catch (_e2) {}
    }
  };

  const MilkItVariantManager = {
    getOptionBySku(sku, root = null) {
      const selectEl = getVariantSelect(root);
      if (!selectEl) return null;
      const safeSku = (sku || '').trim();
      if (!safeSku) return null;
      // Avoid CSS.escape dependency (older iOS Safari)
      return Array.from(selectEl.options).find((o) => (o.dataset.variantSku || '').trim() === safeSku) || null;
    },

    findById(id, root = null) {
      const selectEl = getVariantSelect(root);
      if (!selectEl || id == null) return null;
      const targetId = String(id);
      const opt = Array.from(selectEl.options).find((o) => String(o.dataset.variantId || '') === targetId) || null;
      if (!opt) return null;
      return {
        id: Number(opt.dataset.variantId),
        sku: (opt.dataset.variantSku || '').trim(),
        title: (opt.textContent || '').trim()
      };
    },

    findBySku(sku, root = null) {
      const opt = this.getOptionBySku(sku, root);
      if (!opt) return null;
      return {
        id: Number(opt.dataset.variantId),
        sku: (opt.dataset.variantSku || '').trim(),
        title: (opt.textContent || '').trim()
      };
    },

    updateVariantBySku(sku) {
      const root = getScopeRoot();
      const selectEl = getVariantSelect(root);
      if (!selectEl) return;

      const opt = this.getOptionBySku(sku, root);
      if (!opt) return;
      
      if (selectEl.value === opt.value) return;

      // Set value + selected attr then trigger Dawn's handlers
      selectEl.value = opt.value;
      dispatchChange(selectEl);

      // iOS/Safari: some themes expose updateSelectionMetadata
      try {
        const variantSelectsEl = selectEl.closest('variant-selects');
        if (variantSelectsEl && typeof variantSelectsEl.updateSelectionMetadata === 'function') {
          variantSelectsEl.updateSelectionMetadata({ target: selectEl });
        }
      } catch (_e) {}

      // Dawn pub-sub (optional)
      try {
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
          publish(window.PUB_SUB_EVENTS.variantChange, { data: { variant: { id: Number(opt.dataset.variantId) } } });
        }
      } catch (_e) {}

      // Our own event for any custom listeners
      dispatchVariantChanged(root, { id: Number(opt.dataset.variantId), sku: (opt.dataset.variantSku || '').trim() });
    },

    updateVariantFromCheckboxes() {
      const root = getScopeRoot();
      const eco = qs('#milkit-ecopro-checkbox', root);
      const filter = qs('#milkit-filter-checkbox', root);
      const ecoChecked = !!(eco && eco.checked);
      const filterChecked = !!(filter && filter.checked);

      let sku = 'milk_it';
      if (ecoChecked && filterChecked) sku = 'milk_it_ecopro_set_filter';
      else if (ecoChecked) sku = 'milk_it_ecopro_set';
      else if (filterChecked) sku = 'milk_it_filter';

      this.updateVariantBySku(sku);
    }
  };

  const MilkItPicker = {
    init(root = null) {
      const scope = root || getScopeRoot();
      const container = qs('.milkit-variant-picker', scope);
      if (!container) return;
      if (container.dataset.milkitPickerInitialized === 'true') return;
      container.dataset.milkitPickerInitialized = 'true';

      this.renderCheckboxUI(scope);
      this.syncCheckboxesFromCurrentVariant(scope);
    },

    renderCheckboxUI(root) {
      const scope = root || getScopeRoot();
      const host = qs('.milkit-variant-picker .milkit-variant-select-container', scope) || qs('.milkit-variant-picker', scope);
      if (!host) return;

      host.innerHTML = '';

      // Hide any global top-right details row to avoid duplication (existing theme behavior)
      const topRow = qs('.milkit-picker-toprow', scope);
      if (topRow) topRow.style.display = 'none';

      const t = window.milkitTranslations || {};

      const applyCombinedState = () => {
        MilkItVariantManager.updateVariantFromCheckboxes();
        const eco = qs('#milkit-ecopro-checkbox', scope);
        const filter = qs('#milkit-filter-checkbox', scope);
        const ecoWrapper = eco ? eco.closest('.milkit-variant-select-wrapper') : null;
        const filterWrapper = filter ? filter.closest('.milkit-variant-select-wrapper') : null;
        if (ecoWrapper) ecoWrapper.classList.toggle('checkbox-active', !!(eco && eco.checked));
        if (filterWrapper) filterWrapper.classList.toggle('checkbox-active', !!(filter && filter.checked));
        const rootPicker = (ecoWrapper && ecoWrapper.closest('.milkit-variant-picker')) || (filterWrapper && filterWrapper.closest('.milkit-variant-picker'));
        if (rootPicker) rootPicker.classList.toggle('merge-active', !!(eco && eco.checked && filter && filter.checked));
      };

      const buildRow = (opts) => {
        const wrapper = document.createElement('div');
        wrapper.className = 'milkit-variant-select-wrapper';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = opts.id;

        const content = document.createElement('div');
        content.className = 'milkit-variant-content';

        const inlineRow = document.createElement('div');
        inlineRow.className = 'milkit-inline-row';

        const leftCol = document.createElement('div');
        leftCol.className = 'milkit-left-col';

        const labelRow = document.createElement('div');
        labelRow.className = 'milkit-label-row';

        const label = document.createElement('label');
        label.className = 'milkit-variant-label';
        label.setAttribute('for', opts.id);
        label.textContent = opts.title || '';
        labelRow.appendChild(label);

        leftCol.appendChild(labelRow);
        if (opts.desc) {
          const desc = document.createElement('div');
          desc.className = 'milkit-desc';
          desc.textContent = opts.desc;
          leftCol.appendChild(desc);
        }

        const rightCol = document.createElement('div');
        rightCol.className = 'milkit-right-col';

        const detailsBtn = document.createElement('button');
        detailsBtn.type = 'button';
        detailsBtn.className = (opts.triggerClass || '') + ' compact';
        detailsBtn.setAttribute('aria-haspopup', 'dialog');
        detailsBtn.textContent = opts.detailsLabel || '';
        detailsBtn.addEventListener('click', opts.onDetailsClick);

        rightCol.appendChild(detailsBtn);

        inlineRow.appendChild(leftCol);
        inlineRow.appendChild(rightCol);
        content.appendChild(inlineRow);

        wrapper.appendChild(checkbox);
        wrapper.appendChild(content);
        host.appendChild(wrapper);

        // Toggle when clicking on the left side
        const toggle = (e) => {
          if (e && e.target && (e.target.tagName === 'A' || (e.target.closest && e.target.closest('a')))) return;
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        };
        leftCol.addEventListener('click', (e) => { if (e.target && e.target.tagName === 'LABEL') return; toggle(e); });

        checkbox.addEventListener('change', applyCombinedState);

        return checkbox;
      };

      buildRow({
        id: 'milkit-ecopro-checkbox',
        title: t.checkboxTitle || '',
        desc: t.checkboxDesc || '',
        triggerClass: 'milkit-details-trigger',
        detailsLabel: t.details || '',
        onDetailsClick: () => {
          if (window.milkitDetailsModal && typeof window.milkitDetailsModal.open === 'function') window.milkitDetailsModal.open();
          else {
            const trigger = qs('.milkit-details-trigger', scope);
            if (trigger) trigger.click();
          }
        }
      });

      buildRow({
        id: 'milkit-filter-checkbox',
        title: t.filterCheckboxTitle || '',
        desc: t.filterCheckboxDesc || '',
        triggerClass: 'milkit-filter-details-trigger',
        detailsLabel: t.filterDetails || '',
        onDetailsClick: () => {
          if (window.milkitFilterDetailsModal && typeof window.milkitFilterDetailsModal.open === 'function') window.milkitFilterDetailsModal.open();
        }
      });
    },

    syncCheckboxesFromCurrentVariant(root) {
      const scope = root || getScopeRoot();
      const eco = qs('#milkit-ecopro-checkbox', scope);
      const filter = qs('#milkit-filter-checkbox', scope);
      if (!eco || !filter) return;

      // Try to read SKU from currently selected <option>
      let sku = '';
      const selectEl = getVariantSelect(scope);
      if (selectEl && selectEl.selectedIndex >= 0) {
        const opt = selectEl.options[selectEl.selectedIndex];
        sku = (opt && opt.dataset && opt.dataset.variantSku ? String(opt.dataset.variantSku).trim() : '');
      }

      // If URL has ?variant=, prefer it (and ensure select is in sync)
      try {
        const url = new URL(window.location.href);
        const variantId = url.searchParams.get('variant');
        if (variantId) {
          const v = MilkItVariantManager.findById(variantId, scope);
          if (v && v.sku) {
            MilkItVariantManager.updateVariantBySku(v.sku);
            sku = v.sku;
          }
        }
      } catch (_e) {}

      const setStates = (ecoChecked, filterChecked) => {
        eco.checked = !!ecoChecked;
        filter.checked = !!filterChecked;
        const ecoWrapper = eco.closest('.milkit-variant-select-wrapper');
        const filterWrapper = filter.closest('.milkit-variant-select-wrapper');
        if (ecoWrapper) ecoWrapper.classList.toggle('checkbox-active', eco.checked);
        if (filterWrapper) filterWrapper.classList.toggle('checkbox-active', filter.checked);
        const rootPicker = ecoWrapper ? ecoWrapper.closest('.milkit-variant-picker') : null;
        if (rootPicker) rootPicker.classList.toggle('merge-active', !!(eco.checked && filter.checked));
      };

      switch (sku) {
        case 'milk_it_ecopro_set_filter': setStates(true, true); break;
        case 'milk_it_ecopro_set': setStates(true, false); break;
        case 'milk_it_filter': setStates(false, true); break;
        default: setStates(false, false); break;
      }
    }
  };

  // Expose globals used by other scripts (e.g. milkit-details-modal.js)
  window.MilkItVariantManager = MilkItVariantManager;
  window.MilkItPicker = MilkItPicker;

  const init = () => MilkItPicker.init(getScopeRoot());

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Quick-view injection support
  document.addEventListener('quickview:loaded', init);
})();



